import a from "indefinite";
import { useState } from "react";

import TeamEditDialog from "@apps/org-settings/components/team-edit-dialog";
import { TeamPickerDropdown } from "@apps/reporting/components/team-picker";
import useLabel from "@apps/use-label/use-label";
import { ButtonSize } from "@components/button/button-constants";
import ComboboxGeneric from "@components/combobox/generic-combobox";
import {
  UserComboboxOptionType,
  UserComboboxTeamOption,
} from "@components/user-combobox/user-combobox-list";

// Picks the department something belongs to, and can make a new one without
// leaving the picker.
const DepartmentPickerButton = ({
  organizationId,
  team,
  buttonText,
  ariaLabel,
  onChange,
  className,
  wrapperClassName,
  size = ButtonSize.medium,
  showDownChevronIcon,
}: {
  organizationId: number;
  team: UserComboboxTeamOption | null;
  // Shown instead of the department's name, for a prompt like "Choose…".
  buttonText?: string;
  ariaLabel: string;
  onChange: (team: UserComboboxTeamOption) => void;
  className?: string;
  wrapperClassName?: string;
  size?: ButtonSize;
  // For the form-field version, which reads as a select rather than a button.
  showDownChevronIcon?: boolean;
}) => {
  const label = useLabel();
  const [isCreating, setIsCreating] = useState(false);

  const handleChange = (picked: { id: number; title: string }) =>
    onChange({
      id: picked.id,
      title: picked.title,
      type: UserComboboxOptionType.TEAM,
    });

  return (
    <>
      {isCreating && (
        <TeamEditDialog
          organization={{ id: organizationId }}
          onClose={() => setIsCreating(false)}
          onTeamSaved={(newTeam) => {
            handleChange(newTeam);
            setIsCreating(false);
          }}
        />
      )}
      <TeamPickerDropdown
        teams={team ? [{ id: team.id, title: team.title }] : []}
        onChange={handleChange}
        createLabel={`Create new ${label("team")}`}
        onClickCreateNew={() => setIsCreating(true)}
        className={wrapperClassName}
        organizationId={organizationId}
      >
        <ComboboxGeneric.Button
          size={size}
          className={className}
          showDownChevronIcon={showDownChevronIcon}
          aria-label={ariaLabel}
        >
          {/* Nothing picked yet reads as a placeholder, not as a value. */}
          {buttonText ?? team?.title ?? (
            <span className="text-gray-400">Choose {a(label("team"))}</span>
          )}
        </ComboboxGeneric.Button>
      </TeamPickerDropdown>
    </>
  );
};

export default DepartmentPickerButton;
